import React, { useState } from 'react';
import {
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  DialogContentText,
  TextField
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { PageDealType } from '@types';
import { useUpdatePageDealSocial } from '@hooks/useGetPageDeal';
import { useSnackbar } from '@hooks/useSnackbar';

import { copyTextToClipboard, getPostPrefix } from '@utils/index';

interface SocialPageLinkProps {
  handleClose: () => void;
  pageDeal: PageDealType | null;
}

const SocialPageLink = ({ pageDeal, handleClose }: SocialPageLinkProps) => {
  const [openSocialModal, setOpenSocialModal] = useState(false);
  const [socialTitle, setSocialTitle] = useState('');
  const { showMessage } = useSnackbar();

  const { mutate: updatePageDealSocial } = useUpdatePageDealSocial();

  const downloadCollageImage = async () => {
    const url =
      pageDeal?.socialImage || pageDeal?.collageImage || pageDeal?.imageLarge;
    if (url) {
      try {
        showMessage('Downloading image...');
        // Fetch the image as a Blob
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const blob = await response.blob();

        // Create a temporary URL for the Blob
        const blobUrl = URL.createObjectURL(blob);

        const currentTimeInNumber = new Date().getTime();

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `collage-image-${pageDeal?.slug}-${currentTimeInNumber}.png`; // Specify the file name and extension

        // Programmatically click the link
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        updatePageDealSocial({ id: pageDeal.id, socialTitle });
        handleModalClose();

        copyTextToClipboard(
          `${getPostPrefix()}${socialTitle} #amazonDeals Link in bio`
        );
        showMessage('Copied social text to clipboard');
      } catch (error) {
        console.error('There was a problem downloading the image:', error);
      }
    }
  };

  const handleModalOpen = () => {
    setOpenSocialModal(true);
    setSocialTitle(pageDeal?.title || '');
  };

  const handleModalClose = () => {
    setOpenSocialModal(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={openSocialModal} onClose={handleModalClose}>
        <DialogTitle>Social Image Information</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will determine what it says in the linktree-esque social page.
          </DialogContentText>
          <TextField
            autoFocus
            required
            margin="dense"
            value={socialTitle}
            onChange={(e) => setSocialTitle(e.target.value)}
            id="name"
            name="social-title"
            label="Social Title"
            fullWidth
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button
            onClick={downloadCollageImage}
            variant="contained"
            color="primary"
          >
            Submit & Download
          </Button>
        </DialogActions>
      </Dialog>
      <MenuItem
        key="social-page-link"
        onClick={() => {
          handleModalOpen();
        }}
      >
        <ListItemIcon>
          <DownloadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Download social image</ListItemText>
      </MenuItem>
    </>
  );
};

export default SocialPageLink;
